// Start-up sequence: public shop → LIFF init/login → /me. Provides the ShopContext.
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { type Api, ApiError } from '../api/client.ts';
import type { Me, PublicShop } from '../api/types.ts';
import type { LiffLike } from '../liff/liff.ts';
import { Button, Empty, Loading } from '../ui/index.tsx';

const ATTEMPT_KEY = 'sf_login_attempt';
const ATTEMPT_WINDOW_MS = 2 * 60_000;

export interface ShopContextValue {
  shop: PublicShop;
  me: Me;
  api: Api;
  liff: LiffLike;
  refreshMe(): Promise<Me>;
  isStaff: boolean;
  isOwner: boolean;
}

const ShopContext = createContext<ShopContextValue | null>(null);

export function useShop(): ShopContextValue {
  const v = useContext(ShopContext);
  if (!v) throw new Error('useShop outside ShopBoot');
  return v;
}

/** Exposed for tests that render pages without the boot sequence. */
export const ShopContextProvider = ShopContext.Provider;

type State =
  | { kind: 'loading' }
  | { kind: 'not-found' }
  | { kind: 'redirecting' }
  | { kind: 'reopen' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; shop: PublicShop; me: Me };

function recentAttempt(): boolean {
  const t = Number(sessionStorage.getItem(ATTEMPT_KEY) ?? '0');
  return t > 0 && Date.now() - t < ATTEMPT_WINDOW_MS;
}

export function ShopBoot({ slug, liff, makeApi, children }: {
  slug: string;
  liff: LiffLike;
  makeApi: (opts: { getToken: () => string | null; onAuthError: (code: string) => void }) => Api;
  children: ReactNode;
}) {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const handling = useRef(false);

  /** One silent re-login per window; a repeat failure asks the user to reopen from LINE. */
  const onAuthError = useCallback(() => {
    if (handling.current) return;
    handling.current = true;
    if (recentAttempt()) {
      setState({ kind: 'reopen' });
      return;
    }
    sessionStorage.setItem(ATTEMPT_KEY, String(Date.now()));
    setState({ kind: 'redirecting' });
    liff.relogin(window.location.href);
  }, [liff]);

  const api = useMemo(() => makeApi({ getToken: () => liff.getIDToken(), onAuthError }), [makeApi, liff, onAuthError]);

  useEffect(() => {
    let cancelled = false;
    handling.current = false;
    (async () => {
      setState({ kind: 'loading' });
      let shop: PublicShop;
      try {
        shop = await api.shop();
      } catch (e) {
        if (!cancelled) setState(e instanceof ApiError && e.code === 'SHOP_NOT_FOUND' ? { kind: 'not-found' } : { kind: 'error', message: errText(e) });
        return;
      }
      try {
        if (!shop.liffId) throw new Error('missing LIFF id');
        await liff.init(shop.liffId);
      } catch {
        if (!cancelled) setState({ kind: 'error', message: 'เปิดหน้าไม่สำเร็จ กรุณาลองใหม่' });
        return;
      }
      if (!liff.isLoggedIn()) {
        if (recentAttempt()) { if (!cancelled) setState({ kind: 'reopen' }); return; }
        sessionStorage.setItem(ATTEMPT_KEY, String(Date.now()));
        if (!cancelled) setState({ kind: 'redirecting' });
        liff.login(window.location.href);
        return;
      }
      try {
        const me = await api.me();
        sessionStorage.removeItem(ATTEMPT_KEY);
        if (!cancelled) setState({ kind: 'ready', shop, me });
      } catch (e) {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 401) { onAuthError(); return; } // guarded: runs once
        setState({ kind: 'error', message: errText(e) });
      }
    })();
    return () => { cancelled = true; };
  }, [api, liff, slug, attempt, onAuthError]);

  const refreshMe = useCallback(async () => {
    const me = await api.me();
    setState((s) => (s.kind === 'ready' ? { ...s, me } : s));
    return me;
  }, [api]);

  if (state.kind === 'loading') return <div className="app"><Loading /></div>;
  if (state.kind === 'redirecting') return <div className="app"><Loading text="กำลังเข้าสู่ระบบ LINE…" /></div>;
  if (state.kind === 'not-found') return <div className="app"><Empty title="ไม่พบร้าน" text="ลิงก์อาจไม่ถูกต้อง กรุณาเปิดจากเมนูใน LINE ของร้าน" /></div>;
  if (state.kind === 'reopen') return <div className="app"><Empty title="กรุณาเปิดใหม่จาก LINE" text="ปิดหน้านี้ แล้วกดเมนูในแชท LINE ของร้านอีกครั้ง" /></div>;
  if (state.kind === 'error') {
    return (
      <div className="app">
        <div className="empty">
          <p className="empty-title">{state.message}</p>
          <Button onClick={() => setAttempt((n) => n + 1)}>ลองใหม่</Button>
        </div>
      </div>
    );
  }
  const value: ShopContextValue = {
    shop: state.shop, me: state.me, api, liff, refreshMe,
    isStaff: state.me.staff !== null, isOwner: state.me.staff?.role === 'owner',
  };
  return <ShopContext.Provider value={value}>{children}</ShopContext.Provider>;
}

export function errText(e: unknown): string {
  return e instanceof ApiError ? e.message : 'ระบบขัดข้อง กรุณาลองใหม่';
}
